import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Typography from "@mui/material/Typography";
import { PageContainer } from "@outpave/ds";
import Role from "enums/Role";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import TransactionStatus from "enums/TransactionStatus";
import TransactionsType from "enums/TransactionsType";
import useDebouncedSearch from "hooks/useDebouncedSearch";
import {
  LEDGER_TYPES,
  TRANSFER_STATUSES,
  TRANSFER_TYPES,
} from "utils/transactions/transactionGroups";
import BankTransactionsTable from "./BankTransactionsTable";
import ScheduledTransferFunds from "./ScheduledTransferFunds";

/**
 * Module constants, not inline literals: `useInfiniteScroll` stringifies the
 * variables object to decide when to reset, and an array rebuilt every render
 * makes the table refetch forever.
 */
const ALL_STATUSES = Object.values(TransactionStatus);

const TABS = {
  TRANSACTIONS: "transactions",
  SCHEDULED: "scheduled",
  TRANSFERS: "transfers",
} as const;

type TabId = (typeof TABS)[keyof typeof TABS];

interface BankActivityPanelProps {
  accountId?: string;
  /** The account list is still resolving, so no tab can query yet. */
  loading?: boolean;
  refreshIndex?: number;
  onRowClick?: (transactionId: string) => void;
}

/**
 * The account's activity, in three tabs under one card. Rendered as a flush
 * PageContainer with no header props, which emits the bare tabs, toolbar and
 * table surface the design draws below the two cards, without a page gutter.
 */
const BankActivityPanel: React.FC<BankActivityPanelProps> = ({
  accountId,
  loading,
  refreshIndex,
  onRowClick,
}) => {
  const { t } = useTranslation();
  // upcomingTransfers is held at Admin, a higher bar than the rest of this
  // page. The tab is not offered below it, because ScheduledTransferFunds
  // fires that query the moment it mounts.
  const isAdminOrHigher = useCheckUserRole(Role.Admin);
  const [activeTab, setActiveTab] = useState<TabId>(TABS.TRANSACTIONS);
  const [searchTerm, setSearchTerm] = useState("");
  const search = useDebouncedSearch(setSearchTerm);

  const tabs = [
    { id: TABS.TRANSACTIONS, label: t("Transactions") },
    ...(isAdminOrHigher
      ? [{ id: TABS.SCHEDULED, label: t("Scheduled Transfers") }]
      : []),
    { id: TABS.TRANSFERS, label: t("Transfer History") },
  ];

  // The Scheduled tab can disappear under the user when the role does, and a
  // selection pointing at a tab nobody offers renders an empty panel.
  const visibleTab = tabs.some((tab) => tab.id === activeTab)
    ? activeTab
    : TABS.TRANSACTIONS;

  return (
    <PageContainer
      flush
      tabs={{
        items: tabs,
        activeId: visibleTab,
        onChange: (id) => {
          setActiveTab(id as TabId);
          // A term carried across tabs filters a list the user cannot see.
          setSearchTerm("");
          search.onChange("");
        },
      }}
      // upcomingTransfers takes no searchTerm, and a field that cannot filter
      // is worse than no field.
      search={
        visibleTab === TABS.SCHEDULED
          ? undefined
          : { ...search, placeholder: t("Search") }
      }
    >
      {visibleTab === TABS.TRANSACTIONS && (
        <BankTransactionsTable
          // useInfiniteScroll holds its variables and rows in state and swaps
          // them in an effect, so a mounted table shows the previous account's
          // ledger under the new account's name for a frame. Keying it makes
          // the change a remount, which is synchronous.
          key={`transactions-${accountId}`}
          accountId={accountId}
          searchTerm={searchTerm}
          types={LEDGER_TYPES}
          statuses={ALL_STATUSES}
          refreshIndex={refreshIndex}
          loading={loading}
          emptyState={t("No transactions found")}
          onRowClick={onRowClick}
        />
      )}

      {visibleTab === TABS.SCHEDULED && isAdminOrHigher && (
        <>
          {/* The query declares no account argument, so this list is company
              wide. Saying so beats letting the account picker imply otherwise. */}
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "var(--color-text-tertiary)",
              px: "var(--space-5)",
              pt: "var(--space-3)",
            }}
          >
            {t("ScheduledTransfersAllAccountsNote")}
          </Typography>
          <ScheduledTransferFunds refreshDataIndex={refreshIndex} />
        </>
      )}

      {visibleTab === TABS.TRANSFERS && (
        <BankTransactionsTable
          key={`transfers-${accountId}`}
          accountId={accountId}
          searchTerm={searchTerm}
          types={TRANSFER_TYPES}
          statuses={TRANSFER_STATUSES}
          refreshIndex={refreshIndex}
          loading={loading}
          emptyState={t("No transfers found")}
          onRowClick={onRowClick}
        />
      )}
    </PageContainer>
  );
};

export default BankActivityPanel;
